<template>
  <VcModal
    :title="$t('shared.checkout.select_address_modal.title')"
    max-width="60rem"
    is-mobile-fullscreen
    dividers
    test-id="select-address-modal"
  >
    <VcAlert class="mb-4 lg:hidden" icon="check-circle" size="sm" variant="solid-light">
      {{ $t("shared.checkout.select_address_modal.message") }}
    </VcAlert>

    <template #actions="{ close }">
      <div class="flex w-full flex-col gap-3 md:flex-row md:items-center md:justify-end">
        <div v-if="pages > 1" class="flex w-full min-w-0 flex-col items-center md:items-start">
          <VcPagination v-model:page="page" :pages="Math.min(pages, PAGE_LIMIT)" compact />

          <VcInputDetails v-if="page >= PAGE_LIMIT" :message="$t('ui_kit.reach_limit.page_limit')" />
        </div>

        <div class="flex gap-3 *:flex-1">
          <VcButton
            v-if="allowAddNewAddress && (!isCorporateAddresses || $can($permissions.xApi.CanEditOrganization))"
            variant="outline"
            no-wrap
            min-width="8rem"
            @click="
              $emit('addNewAddress');
              close();
            "
          >
            {{ $t("shared.checkout.select_address_modal.add_address_button") }}
          </VcButton>

          <VcButton
            v-else
            no-wrap
            min-width="8rem"
            color="secondary"
            variant="outline"
            class="flex-none max-md:!hidden"
            data-test-id="close-button"
            @click="close"
          >
            {{ $t("shared.checkout.select_address_modal.cancel_button") }}
          </VcButton>

          <VcButton
            no-wrap
            :disabled="!selectedAddress"
            min-width="8rem"
            data-test-id="confirm-button"
            @click="
              save();
              close();
            "
          >
            {{
              isMobile
                ? $t("shared.checkout.select_address_modal.save_button")
                : $t("shared.checkout.select_address_modal.ok_button")
            }}
          </VcButton>
        </div>
      </div>
    </template>

    <SelectAddressFilter v-if="showFilters" @applyFilter="applyFilter" />

    <div class="rounded border">
      <VcTable
        :columns="columns"
        :items="paginatedAddresses"
        :description="$t('shared.checkout.select_address_modal.meta.table_description')"
        :loading="pickupLocationsLoading"
        @page-changed="onPageChange"
      >
        <template #mobile-item="{ item }">
          <div class="relative grid grid-cols-2 gap-y-4 border-b border-neutral-200 p-6">
            <div class="flex flex-col">
              <span class="text-sm text-neutral-400">
                {{ $t("common.labels.recipient_name") }}
              </span>
              <span class="overflow-hidden text-ellipsis pr-4 font-black">
                {{ 'organization' in item && item.organization ? item.organization : `${item.firstName} ${item.lastName}` }}
              </span>
            </div>

            <div class="flex flex-col">
              <span class="text-sm text-neutral-400">
                {{ $t("common.labels.address") }}
              </span>
              <span class="overflow-hidden text-ellipsis">
                {{ getFormattedAddress(item) }}
              </span>
            </div>

            <div class="flex flex-col">
              <span class="text-sm text-neutral-400">
                {{ $t("common.labels.phone") }}
              </span>
              <span class="overflow-hidden text-ellipsis pr-4">
                {{ item.phone }}
              </span>
            </div>

            <div class="flex flex-col">
              <span class="text-sm text-neutral-400">
                {{ $t("common.labels.email") }}
              </span>
              <span class="overflow-hidden text-ellipsis">
                {{ item.email }}
              </span>
            </div>

            <div class="col-span-2 flex items-center justify-between">
              <div class="flex items-center gap-2 empty:hidden">
                <VcBadge v-if="item.isFavorite" size="sm" variant="outline-dark" rounded color="warning">
                  <VcIcon name="whishlist" />
                  <span>{{ $t("pages.company.info.labels.favorite") }}</span>
                </VcBadge>

                <VcBadge
                  v-if="isEqualAddresses(item, currentAddress ?? {}, { omitFields: props.omitFieldsOnCompare })"
                  size="sm"
                  variant="outline-dark"
                  rounded
                  color="success"
                >
                  <VcIcon name="check" />
                  <span>{{ $t("common.labels.active_address") }}</span>
                </VcBadge>
              </div>

              <div class="text-center">
                <VcIcon v-if="item.id === selectedAddress?.id" class="fill-secondary" name="check-circle" :size="20" />
                <VcButton v-else variant="outline" size="xs" @click="setAddress(item)">
                  {{ $t("shared.checkout.select_address_modal.select_button") }}
                </VcButton>
              </div>
            </div>

            <PickupAvailabilityInfo
              v-if="showAvailability"
              :availability-type="item.availabilityType"
              :availability-note="item.availabilityNote"
              class="col-span-2"
            />
          </div>
        </template>

        <template #mobile-empty>
          <div class="flex items-center space-x-3 border-b border-neutral-200 p-6">
            <span>{{ emptyText ?? $t("shared.checkout.select_address_modal.no_addresses_message") }}</span>

            <VcButton
              v-if="showFilters"
              icon="reset"
              @click="resetFilter"
              :aria-label="$t('pages.account.order_details.bopis.cart_pickup_points_reset_search')"
            />
          </div>
        </template>

        <template #desktop-item="{ item }">
          <tr
            :data-test-id="`customer-address-${item.id}`"
            :class="[
              'group border-b last:border-none hover:bg-secondary-50',
              { 'cursor-pointer': item.id !== selectedAddress?.id },
            ]"
            @click="setAddress(item)"
          >
            <td class="overflow-hidden text-ellipsis px-4 py-3.5">
              <span class="line-clamp-2">
                <VcIcon
                  v-if="hasFavoriteAddresses && item.isFavorite"
                  class="me-1.5 fill-accent"
                  name="whishlist"
                  :size="16"
                />
                {{ 'organization' in item && item.organization ? item.organization : `${item.firstName} ${item.lastName}` }}
              </span>
            </td>

            <td class="overflow-hidden text-ellipsis px-4 py-3.5 [word-break:break-word]">
              <span class="line-clamp-2">
                {{ getFormattedAddress(item) }}
              </span>
            </td>

            <td class="overflow-hidden text-ellipsis truncate px-4 py-3.5">
              {{ item.phone }}
            </td>

            <td class="overflow-hidden text-ellipsis truncate px-4 py-3.5">
              {{ item.email }}
            </td>

            <td v-if="showAvailability" class="truncate px-4 py-3.5">
              <PickupAvailabilityInfo
                :availability-type="item.availabilityType"
                :availability-note="item.availabilityNote"
              />
            </td>

            <td class="h-[3.75rem] py-2.5 text-center">
              <VcIcon v-if="item.id === selectedAddress?.id" class="fill-success" name="check-circle" />

              <VcButton v-else class="invisible group-hover:lg:visible" variant="outline" size="xs">
                {{ $t("shared.checkout.select_address_modal.select_button") }}
              </VcButton>
            </td>
          </tr>
        </template>

        <template #desktop-empty>
          <tr>
            <td :colspan="showAvailability ? 5 : 4">
              <div class="flex flex-col items-center p-5">
                <span class="text-base">
                  {{ emptyText ?? $t("shared.checkout.select_address_modal.no_addresses_message") }}
                </span>

                <VcButton v-if="showFilters" class="mt-5" prepend-icon="reset" @click="resetFilter">
                  {{ $t("pages.account.order_details.bopis.cart_pickup_points_reset_search") }}
                </VcButton>
              </div>
            </td>
          </tr>
        </template>
      </VcTable>
    </div>
  </VcModal>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { computed, watchEffect, ref } from "vue";
import { useI18n } from "vue-i18n";
import { PAGE_LIMIT } from "@/core/constants";
import { isEqualAddresses, isMemberAddressType } from "@/core/utilities";
import { useCartPickupLocations } from "@/shared/cart";
import { SelectAddressFilter } from "@/shared/checkout";
import type { MemberAddressType } from "@/core/api/graphql/types";
import type { AnyAddressType } from "@/core/types";
import PickupAvailabilityInfo from "@/shared/common/components/pickup-availability-info.vue";

interface IProps {
  currentAddress?: AnyAddressType;
  addresses?: AnyAddressType[];
  isCorporateAddresses: boolean;
  allowAddNewAddress?: boolean;
  showAvailability?: boolean;
  emptyText?: string;
  omitFieldsOnCompare?: (keyof MemberAddressType)[];
  showFilters?: boolean;
}

interface IEmits {
  (event: "result", value: AnyAddressType): void;
  (event: "addNewAddress"): void;
  (event: "filterChange"): void;
}

const emit = defineEmits<IEmits>();

const props = withDefaults(defineProps<IProps>(), {
  addresses: () => [],
  allowAddNewAddress: true,
  showAvailability: false,
  showFilters: false,
  omitFieldsOnCompare: () => [],
});

const { t } = useI18n();
const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("md");

const { filterIsApplied, clearFilter, pickupLocationsLoading } = useCartPickupLocations();

function applyFilter() {
  filterIsApplied.value = true;
  page.value = 1;
  emit("filterChange");
}

function resetFilter() {
  clearFilter();
  applyFilter();
}

const selectedAddress = ref<AnyAddressType>();
const page = ref(1);
const itemsPerPage = ref(6);

const pages = computed(() => Math.ceil(props.addresses.length / itemsPerPage.value));
const paginatedAddresses = computed(() =>
  props.addresses.slice((page.value - 1) * itemsPerPage.value, page.value * itemsPerPage.value),
);
const hasFavoriteAddresses = computed(() => props.addresses.some((item) => item.isFavorite));

const columns = computed<ITableColumn[]>(() => {
  // Always use the same columns format like profile addresses page
  const cols: ITableColumn[] = [
    { id: "firstName", title: t("common.labels.recipient_name") },
    { id: "name", title: t("common.labels.address") },
    { id: "phone", title: t("common.labels.phone") },
    { id: "email", title: t("common.labels.email") },
    { id: "id", title: t("common.labels.active_address"), align: "center" },
  ];

  if (props.showAvailability) {
    cols.splice(cols.length - 1, 0, { id: "availability", title: t("pages.account.order_details.bopis.availability") });
  }

  return cols;
});

function getFormattedAddress(address: AnyAddressType): string {
  if (!address) {
    return "";
  }

  // Use RTL-friendly format: regionName، city، line1، line2
  const parts = [address.regionName, address.city, address.line1, address.line2].filter(
    (part) => typeof part === "string" && part.trim() !== "",
  );

  return parts.join("، ");
}

function onPageChange(newPage: number): void {
  page.value = newPage;
}

function setAddress(address: AnyAddressType): void {
  selectedAddress.value = address;
}

function save(): void {
  if (selectedAddress.value) {
    emit("result", selectedAddress.value);
  }
}

watchEffect(() => {
  selectedAddress.value = props.addresses.find((item) =>
    isEqualAddresses(item, props.currentAddress ?? {}, { omitFields: props.omitFieldsOnCompare }),
  );
});
</script>
